import {
    cancelProduct,
    reinstateOrder,
    reinstateProduct,
    updateOrder,
} from "@api";

import { alertActions } from "@features/alert/alertSlice";

import { useAppDispatch } from "@hooks/reduxHooks";

import {
    OrderUpdateHandler,
    SubmitHandler,
    useOrderHandlersProps,
} from "@LeagueManagement/Order/types/order";

const useOrderHandlers = ({
    order,
    setOrder,
    orderStatus,
    orderNote,
    setOrderNote,
}: useOrderHandlersProps) => {
    const dispatch = useAppDispatch();

    const handleOrderUpdate: OrderUpdateHandler = async (
        handleShow,
        handleClose,
    ) => {
        if (orderStatus === "cancelled" && order.status !== "cancelled") {
            handleShow();
            return;
        } else {
            handleSubmit("order", handleClose);
        }
    };

    const handleSubmit: SubmitHandler = async (
        modal,
        closeHandler,
        orderInfo,
    ) => {
        closeHandler();
        let promise: Promise<any> | undefined = undefined;

        switch (modal) {
            case "order":
                promise = updateOrder(
                    order.id,
                    orderStatus,
                    orderNote?.trim() !== "" ? orderNote : undefined,
                );
                break;
            case "product":
                promise = cancelProduct(
                    order.id,
                    orderInfo!.productId,
                    orderInfo!.teamId,
                );
                break;
            case "reinstateProduct":
                promise = reinstateProduct(
                    order.id,
                    orderInfo!.productId,
                    orderInfo!.teamId,
                );
                break;
            case "reinstateOrder":
                promise = reinstateOrder(order.id);
                break;
            default:
                dispatch(
                    alertActions.error({
                        title: `An internal problem occurred.`,
                        text: "Please contact Mark.",
                        dismissible: true,
                    }),
                );
                break;
        }

        if (!promise) return;

        await promise
            .then(response => {
                setOrder(response.order);
                dispatch(
                    alertActions.success({
                        title: `Order updated successfully`,
                        dismissible: true,
                    }),
                );
                setOrderNote(undefined);
            })
            .catch(e => {
                if (
                    modal === "reinstateProduct" ||
                    modal === "reinstateOrder"
                ) {
                    const serverMessage =
                        typeof e === "string" && e.trim() !== ""
                            ? e
                            : undefined;
                    dispatch(
                        alertActions.error({
                            title: `The server encountered a problem reinstating the ${
                                modal === "reinstateOrder"
                                    ? "order"
                                    : "registration"
                            }.`,
                            text:
                                serverMessage ??
                                "If the problem continues, please reach out to an admin.",
                            dismissible: true,
                        }),
                    );
                    return;
                }
                dispatch(
                    alertActions.error({
                        title: `The server encountered a problem updating the order.`,
                        text: "If the problem continues, please reach out to an admin.",
                        dismissible: true,
                    }),
                );
            });
    };

    return {
        handleSubmit,
        handleOrderUpdate,
    };
};

export default useOrderHandlers;
